import React, { useState, useEffect } from "react"
import Swal from "sweetalert2"
import { ROOT_API_URL } from "../config/apiConfig"
import Footer from "../components/Footer"
import { useOutletContext, useLocation, Link, useNavigate } from "react-router-dom"
import { confirmBooking, downloadTravelDocument, preValidateBookingLimits } from "../api/bookingApi"
import { isServerPriceCorrection, applyServerPriceCorrection } from "../utils/serverPriceCorrection"


const SearchResultPaasenger = () => {
  const { companyInfo } = useOutletContext()
  const location = useLocation()
  const { searchResults: initialSearchResults, searchParams, ports, cabins, payloadTypes } = location.state || {}
  const [searchResults, setSearchResults] = useState(initialSearchResults)

  const [selectedTrips, setSelectedTrips] = useState([])
  const [expandedTrips, setExpandedTrips] = useState({})

  const [countries, setCountries] = useState([])

  const navigate = useNavigate()
  const [passengerFormData, setPassengerFormData] = useState({})
  const [isBooking, setIsBooking] = useState(false)
  const isB2C = localStorage.getItem("authToken") && !localStorage.getItem("companyAuthToken");

  const handlePassengerChange = (pIdx, idx, field, value, typeName) => {
    const key = `${pIdx}-${idx}`
    const updates = { [field]: value, passengerType: typeName }

    // Auto-calculate age when Date of Birth is selected
    if (field === "birthDate" && value) {
      const today = new Date()
      const dob = new Date(value)
      let age = today.getFullYear() - dob.getFullYear()
      const monthDiff = today.getMonth() - dob.getMonth()
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        age--
      }
      updates.age = age >= 0 ? age : 0
    }

    setPassengerFormData(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        ...updates
      }
    }))
  }


  const handlePreValidateBooking = async () => {
    const token = localStorage.getItem("companyAuthToken") || localStorage.getItem("authToken")
    if (!token) {
      Swal.fire({ icon: 'warning', title: 'Not Logged In', text: 'Authentication token not found. Please log in.' })
      return
    }

    if (selectedTrips.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No Trip Selected', text: 'Please select a trip before booking.' })
      return
    }

    const passengersCount = (searchParams?.passengers || []).reduce(
      (sum, passenger) => sum + Number(passenger.quantity || 0),
      0
    )

    if (passengersCount <= 0) {
      Swal.fire({ icon: 'error', title: 'Booking Blocked', text: 'No passengers were selected for this booking.' })
      return
    }

    if (isReturnTrip) {
      const outboundKey = selectedTrips.find(key => !key.startsWith("inbound-"))
      const inboundKey = selectedTrips.find(key => key.startsWith("inbound-"))
      if (
        !outboundKey ||
        !inboundKey ||
        selectedPricingType[outboundKey] !== selectedPricingType[inboundKey]
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Blocked',
          text: 'Select both outbound and inbound using the same fare type.'
        })
        return
      }
    }

    let totalFareAggregate = 0
    let currencyCode = null

    for (const key of selectedTrips) {
      const isInbound = key.startsWith("inbound-")
      const tripCabinKey = isInbound ? key.slice("inbound-".length) : key
      const separatorIndex = tripCabinKey.lastIndexOf("-")
      const tripId = tripCabinKey.slice(0, separatorIndex)
      const cabinId = tripCabinKey.slice(separatorIndex + 1)
      const tripOption = displayTrips.find(option => {
        const selectedTrip = isInbound ? option.returnTrip?.inbound : (option.trip || option.returnTrip?.outbound)
        return selectedTrip?._id === tripId && option.cabin?._id === cabinId
      })
      const selectedType = selectedPricingType[key]
      const activePricing = selectedType === "refundable"
        ? tripOption?.pricing?.refundable
        : selectedType === "nonRefundable"
          ? tripOption?.pricing?.nonRefundable
          : null
      const legTotal = isReturnTrip
        ? (isInbound ? activePricing?.inboundTotal : activePricing?.outboundTotal)
        : activePricing?.totalPrice
      const legCurrencyCode = activePricing?.currency?.currencyCode

      if (!tripOption || !activePricing || !Number.isFinite(Number(legTotal)) || Number(legTotal) <= 0 || !legCurrencyCode) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Blocked',
          text: `Pricing could not be resolved for the selected ${isInbound ? "inbound" : "outbound"} trip. Please select the fare again.`
        })
        return
      }

      if (currencyCode && currencyCode !== legCurrencyCode) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Blocked',
          text: 'Selected trip fares use different currencies. Please select matching fares.'
        })
        return
      }

      currencyCode = legCurrencyCode
      totalFareAggregate += Number(legTotal)
    }

    const totalPassengersAggregate = passengersCount * selectedTrips.length

    Swal.fire({
      title: 'Validating booking limits...',
      text: 'Checking your hierarchy balances...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    })

    const payload = {
      totalFare: totalFareAggregate,
      currencyCode,
      requiredTickets: totalPassengersAggregate
    }

    try {
      const response = await preValidateBookingLimits(token, payload)
      if (response && response.success) {
        Swal.close()
        // Validation passed, open the modal programmatically
        const modalElement = document.getElementById('myModal1')
        if (modalElement) {
          const modal = new window.bootstrap.Modal(modalElement)
          modal.show()
        }
      } else {
        Swal.fire({ icon: 'error', title: 'Booking Blocked', text: response?.message || "Hierarchy validation failed." })
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message || "An error occurred during validation." })
    }
  }

  const onSubmitBooking = async (isTemporary = false) => {

    try {
      const token = localStorage.getItem("companyAuthToken") || localStorage.getItem("authToken")
      if (!token) {
        Swal.fire({ icon: 'warning', title: 'Not Logged In', text: 'Authentication token not found. Please log in.' })
        return
      }

      if (selectedTrips.length === 0) {
        Swal.fire({ icon: 'warning', title: 'No Trip Selected', text: 'Please select a trip before booking.' })
        return
      }

      const isReturnTrip = searchParams?.tripType === 'return'
      const outboundKey = selectedTrips.find(key => !key.startsWith("inbound-"))
      const inboundKey = selectedTrips.find(key => key.startsWith("inbound-"))

      if (!outboundKey || (isReturnTrip && !inboundKey)) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Failed',
          text: isReturnTrip
            ? 'Select both outbound and inbound fares before booking.'
            : 'Select an outbound fare before booking.'
        })
        return
      }

      const parseTripCabinKey = (key, prefix = "") => {
        const value = prefix ? key.slice(prefix.length) : key
        const separatorIndex = value.lastIndexOf("-")
        return {
          tripId: value.slice(0, separatorIndex),
          cabinId: value.slice(separatorIndex + 1)
        }
      }

      const { tripId: outboundTripId, cabinId } = parseTripCabinKey(outboundKey)
      const inboundSelection = inboundKey
        ? parseTripCabinKey(inboundKey, "inbound-")
        : null
      const outboundTripObj = displayTrips.find(option => {
        const trip = option.trip || option.returnTrip?.outbound
        return trip?._id === outboundTripId && option.cabin?._id === cabinId
      })
      const inboundTripObj = inboundSelection
        ? displayTrips.find(option =>
            option.returnTrip?.inbound?._id === inboundSelection.tripId &&
            option.cabin?._id === inboundSelection.cabinId
          )
        : null
      const outboundTrip = outboundTripObj?.trip || outboundTripObj?.returnTrip?.outbound
      const inboundTrip = inboundTripObj?.returnTrip?.inbound

      if (!outboundTripObj || !outboundTrip || (isReturnTrip && (!inboundTripObj || !inboundTrip))) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Failed',
          text: 'The selected trip details could not be resolved. Please select the fares again.'
        })
        return
      }

      const departureDate = new Date(outboundTrip.departureDateTime)

      const passengersArray = Object.values(passengerFormData)

      let expectedPassengers = 0
      searchParams?.passengers?.forEach(p => {
        expectedPassengers += p.quantity
      })

      if (passengersArray.length < expectedPassengers) {
        Swal.fire({ icon: 'warning', title: 'Incomplete Details', text: 'Please fill in all passenger details.' })
        return
      }

      for (const passenger of passengersArray) {
        if (!passenger.birthDate) {
          Swal.fire({ icon: 'warning', title: 'Incomplete Details', text: `Please select a valid birth date for all passengers.` })
          return
        }

        const birthDate = new Date(passenger.birthDate)
        let age = departureDate.getFullYear() - birthDate.getFullYear()
        const m = departureDate.getMonth() - birthDate.getMonth()
        if (m < 0 || (m === 0 && departureDate.getDate() < birthDate.getDate())) {
          age--
        }

        if (age < 0) {
          Swal.fire({ icon: 'error', title: 'Invalid Birth Date', text: `Passenger ${passenger.name || 'Passenger'}'s birth date cannot be after the trip departure date.` })
          return;
        }

        // Validate that manually entered age matches calculated age from Date of Birth
        const enteredAge = parseInt(passenger.age)
        if (isNaN(enteredAge)) {
          Swal.fire({ icon: 'warning', title: 'Incomplete Details', text: `Please enter the age for Passenger ${passenger.name || ''}.` })
          return;
        }

        if (enteredAge !== age) {
          Swal.fire({
            icon: 'error',
            title: 'Age Mismatch',
            text: `Passenger ${passenger.name || 'Passenger'}'s entered age (${enteredAge}) does not match their actual age (${age}) calculated from Date of Birth (${passenger.birthDate}).`
          })
          return;
        }

        // Dynamic age validation based on PayloadType snapshot
        const payloadTypeDef = outboundTripObj?.passengers?.find(p => p.payloadType?.name === passenger.passengerType)?.payloadType;
        if (payloadTypeDef && payloadTypeDef.ageRange) {
          const { from, to } = payloadTypeDef.ageRange;
          if (from !== undefined && from !== null && age < from) {
            Swal.fire({ icon: 'error', title: 'Age Validation Failed', text: `Passenger ${passenger.name || 'Passenger'} is ${age} years old, but the minimum age for a ${passenger.passengerType} is ${from}.` })
            return;
          }
          if (to !== undefined && to !== null && age > to) {
            Swal.fire({ icon: 'error', title: 'Age Validation Failed', text: `Passenger ${passenger.name || 'Passenger'} is ${age} years old, but the maximum age for a ${passenger.passengerType} is ${to}.` })
            return;
          }
        }
      }

      const outboundPricingType = selectedPricingType[outboundKey]
      const inboundPricingType = inboundKey ? selectedPricingType[inboundKey] : null

      if (
        !["refundable", "nonRefundable"].includes(outboundPricingType) ||
        (isReturnTrip && inboundPricingType !== outboundPricingType)
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Failed',
          text: isReturnTrip
            ? 'Outbound and inbound must use the same selected fare type.'
            : 'The selected fare type could not be resolved. Please select the fare again.'
        })
        return
      }

      const activePricing = outboundTripObj.pricing?.[outboundPricingType]
      const totalFare = isReturnTrip ? activePricing?.returnTotalPrice : activePricing?.totalPrice
      const baseFare = activePricing?.totalBasicPrice
      const currencyId = activePricing?.currency?._id || (
        typeof activePricing?.currency === "string" ? activePricing.currency : null
      )

      if (
        !activePricing ||
        !Number.isFinite(Number(totalFare)) ||
        Number(totalFare) <= 0 ||
        !Number.isFinite(Number(baseFare)) ||
        Number(baseFare) <= 0 ||
        !currencyId
      ) {
        Swal.fire({
          icon: 'error',
          title: 'Booking Failed',
          text: 'The selected fare has incomplete pricing or currency data. Please contact the company.'
        })
        return
      }

      const payload = {
        category: "passenger",
        bookingType: isReturnTrip ? "Return" : "OneWay",
        bookingSource: "Agent",
        outboundTrip: outboundTripId,
        returnTrip: isReturnTrip ? inboundSelection.tripId : null,
        originPort: searchParams?.originPort,
        destinationPort: searchParams?.destinationPort,
        departureDate: outboundTrip.departureDateTime,
        returnDate: isReturnTrip ? inboundTrip.departureDateTime : null,
        cabin: cabinId,
        visaType: (searchParams?.visaType && searchParams.visaType.toLowerCase() !== "all") ? searchParams.visaType : (outboundTripObj?.pricing?.refundable?.breakdown?.[0]?.visaType || outboundTripObj?.pricing?.visaType || "All"),
        currency: currencyId,
        baseFare: baseFare,
        totalFare: totalFare,
        isTemporary: !!isTemporary,
        passengers: passengersArray.map(p => ({
          passengerName: p.name,
          gender: p.gender,
          birthDate: p.birthDate,
          nationality: p.nationality,
          passportNo: p.passportNo,
          passengerType: p.passengerType,
          passportExpiry: p.passportExpiryDate,
          email: p.email
        })),
        snapshot: {
          trip: outboundTrip,
          returnTrip: isReturnTrip ? inboundTrip : null,
          cabin: outboundTripObj?.cabin,
          passengers: outboundTripObj?.passengers,
          pricing: outboundTripObj?.pricing,
          ticketingRules: outboundTripObj?.ticketingRules,
          availability: outboundTripObj?.availability,
          selectedPricingType: outboundPricingType
        }
      }

      setIsBooking(true)
      const idempotencyKey = Date.now().toString() + Math.random().toString(36).substring(7)
      let response = await confirmBooking(token, payload, idempotencyKey)

      // Server-authoritative pricing: if the backend recomputed a different price, show the
      // corrected amount and re-submit once with the server values after the customer confirms.
      if (isServerPriceCorrection(response)) {
        const accepted = await applyServerPriceCorrection(response, payload)
        if (!accepted) return
        response = await confirmBooking(token, payload, idempotencyKey)
      }

      if (response && response.success) {
        let successTitle = isTemporary ? 'Booking Placed on Hold!' : 'Booking Confirmed!';
        let successText = isTemporary
          ? 'Your booking is temporarily held for 15 minutes. Please complete payment in the booking list.'
          : 'Your booking has been created successfully.';

        if (isB2C) {
          successTitle = 'Booking Request Received!';
          successText = 'Your booking is placed on hold pending review and confirmation by the company.';
        }

        const createdBooking = response.data?.booking || response.data
        const canDownloadTravelDocument = !isTemporary && !isB2C && createdBooking?._id
        let downloadError = null
        if (canDownloadTravelDocument) {
          try {
            await downloadTravelDocument(
              token,
              createdBooking._id,
              createdBooking.travelDocumentNo || createdBooking.bookingReference
            )
          } catch (error) {
            downloadError = error
          }
        }
        Swal.fire({
          icon: downloadError ? 'warning' : 'success',
          title: downloadError ? 'Booking Confirmed' : successTitle,
          text: downloadError ? (downloadError.message || 'Booking confirmed, but travel document download failed.') : (canDownloadTravelDocument ? 'Travel document download started.' : successText),
          timer: 3000,
          showConfirmButton: false,
          confirmButtonColor: '#0ea5e9',
          cancelButtonColor: '#6c757d',
        }).then(() => {
          const closeBtn = document.querySelector("#myModal1 .btn-close")
          if (closeBtn) closeBtn.click()

          // Show live update of seat availability
          const updated = JSON.parse(JSON.stringify(searchResults))
          if (updated?.data?.trips) {
            updated.data.trips.forEach(tripItem => {
              // OneWay structure
              if (tripItem.cabinOptions) {
                const tripId = tripItem.trip?._id
                tripItem.cabinOptions.forEach(opt => {
                  const key = `${tripId}-${opt.cabin?._id}`
                  if (selectedTrips.includes(key) && opt.availability) {
                    opt.availability.finalAvailableSeats = Math.max(0, (opt.availability.finalAvailableSeats || 0) - expectedPassengers)
                    opt.availability.availableSeats = Math.max(0, (opt.availability.availableSeats || 0) - expectedPassengers)
                  }
                })
              }
              // ReturnTrip structure
              else if (tripItem.returnTrip) {
                const outId = tripItem.returnTrip.outbound?._id
                const key = `${outId}-${tripItem.cabin?._id}`
                if (selectedTrips.includes(key) && tripItem.availability) {
                  tripItem.availability.outboundSeats = Math.max(0, (tripItem.availability.outboundSeats || 0) - expectedPassengers)
                  tripItem.availability.inboundSeats = Math.max(0, (tripItem.availability.inboundSeats || 0) - expectedPassengers)
                }
              }
            })
          }
          setSearchResults(updated)

          setSelectedTrips([])
          setSelectedPricingType({})
          setPassengerFormData({})

          // Update location state so a browser refresh doesn't restore old availability
          navigate(location.pathname, { replace: true, state: { ...location.state, searchResults: updated } })
        })
      } else {
        Swal.fire({ icon: 'error', title: 'Booking Failed', text: response?.message || "Unknown error" })
      }
    } catch (err) {
      console.error("Booking error:", err)
      Swal.fire({ icon: 'error', title: 'Error', text: err.response?.data?.message || err.message || "An error occurred while creating the booking." })
    } finally {
      setIsBooking(false)
    }
  }


  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await fetch(`${ROOT_API_URL}/api/public/countries`)
        const data = await response.json()
        if (data && data.success && data.data) {
          setCountries(data.data.countries || [])
        }
      } catch (err) {
        console.error("Error fetching countries:", err)
      }
    }
    fetchCountries()
  }, [])


  // Format date time for display
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    })
  }

  // Get port name by ID
  const getPortName = (portId) => {
    const port = ports?.find((p) => p._id === portId)
    return port?.name || portId
  }

  // Get cabin name by ID
  const getCabinName = (cabinId) => {
    const cabin = cabins?.find((c) => c._id === cabinId)
    return cabin?.name || cabinId
  }

  // Get passenger summary
  const getPassengerSummary = () => {
    if (!searchParams?.passengers || !payloadTypes) return "N/A"
    return searchParams.passengers
      .map((p) => {
        const type = payloadTypes.find((t) => t._id === p.payloadTypeId)
        return `${p.quantity} ${type?.name || "Passenger"}`
      })
      .join(", ")
  }

  // Track selected pricing type for each trip
  const [selectedPricingType, setSelectedPricingType] = React.useState({})

  // Toggle trip selection with pricing type
  const toggleTripSelection = (tripId, cabinId, pricingType) => {
    const key = `${tripId}-${cabinId}`
    const isInboundSelection = key.startsWith("inbound-")
    const alreadySelected = selectedTrips.includes(key)

    setSelectedTrips((prev) => {
      if (prev.includes(key)) {
        return prev.filter((k) => k !== key)
      }

      if (isReturnTrip && isInboundSelection) {
        return [...prev.filter((k) => !k.startsWith("inbound-")), key]
      }

      if (isReturnTrip) {
        return [...prev.filter((k) => k.startsWith("inbound-")), key]
      }

      return [...prev, key]
    })

    setSelectedPricingType((prev) => {
      const next = { ...prev }
      if (alreadySelected) {
        delete next[key]
        return next
      }

      if (isReturnTrip && isInboundSelection) {
        Object.keys(next).forEach((existingKey) => {
          if (existingKey.startsWith("inbound-")) delete next[existingKey]
        })
      } else if (isReturnTrip) {
        Object.keys(next).forEach((existingKey) => {
          if (!existingKey.startsWith("inbound-")) delete next[existingKey]
        })
      }

      next[key] = pricingType
      return next
    })
  }

  // Toggle trip details expansion
  const toggleExpand = (tripId) => {
    setExpandedTrips((prev) => ({
      ...prev,
      [tripId]: !prev[tripId],
    }))
  }

  // Determine trip type and extract trips appropriately
  const isReturnTrip = searchParams?.tripType === "return"
  let displayTrips = []

  if (isReturnTrip && Array.isArray(searchResults?.data?.trips)) {
    displayTrips = searchResults.data.trips.map((tripCombination) => ({
      returnTrip: {
        outbound: tripCombination.returnTrip?.outbound,
        inbound: tripCombination.returnTrip?.inbound,
      },
      cabin: tripCombination.cabin,
      availability: tripCombination.availability,
      pricing: tripCombination.pricing,
      passengers: tripCombination.passengers,
      bookingAllowed: tripCombination.bookingAllowed,
      totalPassengers: tripCombination.totalPassengers,
      ticketingRules: tripCombination.ticketingRules,
    })) || []
  } else if (!isReturnTrip && Array.isArray(searchResults?.data?.trips)) {
    displayTrips = searchResults.data.trips.flatMap((tripItem) =>
      (tripItem.cabinOptions || []).map((option) => ({
        trip: tripItem.trip,
        cabin: option.cabin,
        availability: option.availability,
        pricing: option.pricing,
        bookingAllowed: option.bookingAllowed,
        passengers: tripItem.passengers,
        totalPassengers: tripItem.totalPassengers,
        ticketingRules: tripItem.ticketingRules
      }))
    )
  }

  const getReturnSelectionKey = (tripData, direction) => {
    const trip = direction === "inbound" ? tripData.returnTrip?.inbound : tripData.returnTrip?.outbound
    const cabin = tripData.cabin
    if (!trip?._id || !cabin?._id) return null
    const key = `${trip._id}-${cabin._id}`
    return direction === "inbound" ? `inbound-${key}` : key
  }

  const getUniqueReturnTrips = (direction) => {
    const seen = new Set()
    return displayTrips.filter((tripData) => {
      const key = getReturnSelectionKey(tripData, direction)
      if (!key || seen.has(key)) return false
      seen.add(key)
      return true
    })
  }

  const outboundDisplayTrips = isReturnTrip ? getUniqueReturnTrips("outbound") : displayTrips
  const inboundDisplayTrips = isReturnTrip ? getUniqueReturnTrips("inbound") : []

  const hasSelectedClosedTrip = displayTrips.some((tripData) => {
    const trip = isReturnTrip ? tripData.returnTrip?.outbound : tripData.trip
    const cabin = tripData.cabin
    if (!trip || !cabin) return false
    const key = `${trip._id}-${cabin._id}`
    return selectedTrips.includes(key) && !tripData.bookingAllowed
  })

  const hasResults = displayTrips.length > 0

  return (
    <>
      <section className="features py-5">
        <div className="container-lg">
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .table>:not(caption)>*>* {
                  caption-side: bottom;
                  border-collapse: collapse;
                  background-color: var(--fcard)!important;
                }
                .table th {
                  vertical-align: bottom;
                  background-color: var(--bg-body)!important;
                  color:var(--text-color) !important;
                  white-space: nowrap;
                }
                .modal-content {
                  background:var(--bg-body);
                }
                .trip-details-expanded {
                  background-color: #f8f9fa;
                  border-left: 3px solid #0d6efd;
                }
                .price-breakdown {
                  background-color: #fff;
                  border-radius: 8px;
                }
                .status-badge {
                  padding: 4px 8px;
                  border-radius: 4px;
                  font-size: 0.75rem;
                  font-weight: 600;
                }
                .status-scheduled { background-color: #d1e7dd; color: #0f5132; }
                .status-departed { background-color: #f8d7da; color: #842029; }
                .status-boarding { background-color: #fff3cd; color: #664d03; }
              `,
            }}
          />
          <div className="row g-3 justify-content-center">
            <div className="col-lg-12">
              <h1 className="mb-4">Search Results</h1>

              {/* Search Summary */}
              {searchParams && (
                <div className="card shadow-sm custom-card mb-4">
                  <div className="card-body">
                    <p className="text-muted mb-0">
                      <strong>{searchParams.tripType === "return" ? "Round-trip" : "One-way"} Passenger trip</strong> from{" "}
                      <strong>{getPortName(searchParams.originPort)}</strong> to{" "}
                      <strong>{getPortName(searchParams.destinationPort)}</strong> departing on{" "}
                      <strong>{searchParams.departureDate}</strong>
                      {searchParams.tripType === "return" && searchParams.returnDate && (
                        <> and returning on <strong>{searchParams.returnDate}</strong></>
                      )}
                      . Cabin: <strong>{getCabinName(searchParams.cabin)}</strong>. Visa Type:{" "}
                      <strong>{searchParams.visaType}</strong>. Passengers: <strong>{getPassengerSummary()}</strong>.
                    </p>
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-end mb-4">
                <button
                  className="btn btn-primary-navy me-2 custom-blue-btn"
                  onClick={handlePreValidateBooking}
                  disabled={selectedTrips.length === 0 || hasSelectedClosedTrip}
                >
                  {hasSelectedClosedTrip ? "Booking Closed" : `Book Selected (${selectedTrips.length})`}
                </button>
                <Link to=".." className="btn btn-secondary">
                  Back to Search
                </Link>
              </div>

              {!hasResults ? (
                <div className="card shadow-sm custom-card">
                  <div className="card-body text-center py-5">
                    <h4 className="text-muted">No trips found</h4>
                    <p className="text-muted">Try adjusting your search criteria</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Outbound Trip Section */}
                  <div className="card shadow-sm custom-card mb-4">
                    <div className="card-body p-0">
                      <h3 className="card-title p-3 mb-0 border-bottom">
                        {isReturnTrip ? "Outbound Trip" : "Trip"} (
                          {outboundDisplayTrips.length} found)
                      </h3>
                      <div className="table-responsive">
                        <table className="table table-borderless align-middle mb-0 custom-results-table">
                          <thead className="text-secondary text-uppercase" style={{ fontSize: "0.8rem" }}>
                            <tr>
                              <th>Ship Name</th>
                              <th>Trip Code</th>
                              <th>Origin</th>
                              <th>Destination</th>
                              <th>ETD</th>
                              <th>ETA</th>
                              <th>Cabin</th>
                              <th>Passenger</th>
                              <th>Visa</th>
                              <th>Available</th>
                              <th>Total Price</th>
                              <th>Status</th>
                              <th>Select</th>
                            </tr>
                          </thead>
                          <tbody>
                            {outboundDisplayTrips.map((tripData, index) => {
                              const trip = isReturnTrip ? tripData.returnTrip?.outbound : tripData.trip
                              const cabin = tripData.cabin
                              const availability = tripData.availability
                              const pricing = tripData.pricing
                              const passengers = tripData.passengers
                              const ticketingRules = isReturnTrip
                                ? (tripData.ticketingRules?.outbound || {})
                                : (tripData.ticketingRules || {})
                              const bookingAllowed = tripData.bookingAllowed
                              const pricingType = tripData.pricingType

                              if (!trip) return null

                              // Outbound deduplication: for return trips, show each outbound trip+cabin only once.
                              // displayTrips is NOT modified — all .find() lookups in booking/pre-validation use the full array.
                              const getPricingBadge = () => {
                                if (pricingType === 'refundable') {
                                  return { label: 'Refundable ✓', bgColor: 'bg-success', textColor: 'text-white' }
                                } else if (pricingType === 'nonRefundable') {
                                  return { label: 'Non-Refundable', bgColor: 'bg-warning', textColor: 'text-dark' }
                                }
                                return { label: 'Standard', bgColor: 'bg-secondary', textColor: 'text-white' }
                              }
                              const pricingBadge = getPricingBadge()

                              return (
                                <React.Fragment key={`outbound-${trip._id}-${cabin._id}-${pricingType}-${index}`}>
                                  <tr className="trip-row">
                                    <td>
                                      <strong>{trip.ship?.name || "N/A"}</strong>
                                      <br />
                                      <small className="text-muted">{trip.ship?.imoNumber}</small>
                                    </td>
                                    <td>{trip.tripCode}</td>
                                    <td>
                                      {trip.departurePort?.name}
                                      <br />
                                      <small className="text-muted">({trip.departurePort?.code})</small>
                                    </td>
                                    <td>
                                      {trip.arrivalPort?.name}
                                      <br />
                                      <small className="text-muted">({trip.arrivalPort?.code})</small>
                                    </td>
                                    <td>{formatDateTime(trip.departureDateTime)}</td>
                                    <td>{formatDateTime(trip.arrivalDateTime)}</td>
                                    <td>
                                      <strong>{cabin.name}</strong>
                                    </td>
                                    <td>
                                      {passengers?.map((p) => `${p.quantity} ${p.payloadType?.name}`).join(", ") || "N/A"}
                                    </td>
                                    <td>
                                      {searchParams?.visaType || "N/A"}
                                    </td>
                                    <td>
                                      {(() => {
                                        const availSeats = isReturnTrip
                                          ? availability?.outboundSeats ?? 0
                                          : availability?.finalAvailableSeats ?? availability?.availableSeats ?? 0
                                        return (
                                          <span
                                            className={`badge ${availSeats > 10
                                              ? "bg-success"
                                              : availSeats > 0
                                                ? "bg-warning"
                                                : "bg-danger"
                                              }`}
                                          >
                                            {availSeats}
                                          </span>
                                        )
                                      })()}
                                    </td>
                                    <td>
                                      {pricing ? (
                                        <div className="d-flex gap-2" style={{ minHeight: '70px' }}>
                                          {/* Refundable Price Card — outbound: only show if outboundTotal > 0 */}
                                          {pricing.refundable && pricing.refundable.available &&
                                            (!isReturnTrip || (pricing.refundable.outboundTotal ?? 0) > 0) && (
                                              <div
                                                className="flex-fill p-2 d-flex flex-column justify-content-center"
                                                style={{
                                                  border: selectedPricingType[`${trip._id}-${cabin._id}`] === 'refundable' ? '3px solid #28a745' : '1px solid #28a745',
                                                  borderRadius: '6px',
                                                  backgroundColor: '#f0f9f7',
                                                  cursor: 'pointer',
                                                  transition: 'all 0.2s'
                                                }}
                                                onClick={() => toggleTripSelection(trip._id, cabin._id, 'refundable')}
                                                role="button"
                                                tabIndex={0}
                                              >
                                                <span className="badge bg-success text-white mb-1" style={{ width: 'fit-content' }}>Refundable ✓</span>
                                                <strong style={{ fontSize: '1.1em' }}>
                                                  {pricing.refundable.currency?.currencyCode || ""}{" "}
                                                  {isReturnTrip
                                                    ? (pricing.refundable.outboundTotal || 0).toFixed(2)
                                                    : (pricing.refundable.totalPrice || 0).toFixed(2)
                                                  }
                                                </strong>
                                                {selectedPricingType[`${trip._id}-${cabin._id}`] === 'refundable' && (
                                                  <span className="badge bg-success mt-2" style={{ width: 'fit-content' }}>Selected</span>
                                                )}
                                              </div>
                                            )}

                                          {/* Non-Refundable Price Card — outbound: only show if outboundTotal > 0 */}
                                          {pricing.nonRefundable &&
                                            (isReturnTrip
                                              ? (pricing.nonRefundable.outboundTotal ?? 0) > 0
                                              : (pricing.nonRefundable.available || pricing.nonRefundable.totalPrice > 0)) && (
                                              <div
                                                className="flex-fill p-2 d-flex flex-column justify-content-center"
                                                style={{
                                                  border: selectedPricingType[`${trip._id}-${cabin._id}`] === 'nonRefundable' ? '3px solid #ffc107' : '1px solid #ffc107',
                                                  borderRadius: '6px',
                                                  backgroundColor: '#fff8f0',
                                                  cursor: 'pointer',
                                                  transition: 'all 0.2s'
                                                }}
                                                onClick={() => toggleTripSelection(trip._id, cabin._id, 'nonRefundable')}
                                                role="button"
                                                tabIndex={0}
                                              >
                                                <div className="mb-1">
                                                  <span className="badge bg-warning text-dark" style={{ width: 'fit-content' }}>Non-Refundable</span>
                                                  {pricing.nonRefundable.hasMissingPrice && (
                                                    <span className="badge bg-danger text-white ms-1">Partial</span>
                                                  )}
                                                </div>
                                                <strong style={{ fontSize: '1.1em' }}>
                                                  {pricing.nonRefundable.currency?.currencyCode || ""}{" "}
                                                  {isReturnTrip
                                                    ? (pricing.nonRefundable.outboundTotal || 0).toFixed(2)
                                                    : (pricing.nonRefundable.totalPrice || 0).toFixed(2)
                                                  }
                                                </strong>
                                                {selectedPricingType[`${trip._id}-${cabin._id}`] === 'nonRefundable' && (
                                                  <span className="badge bg-warning text-dark mt-2" style={{ width: 'fit-content' }}>Selected</span>
                                                )}
                                              </div>
                                            )}

                                          {/* Fallback for single pricing */}
                                          {(!pricing.refundable || !pricing.refundable.available) &&
                                            (!pricing.nonRefundable || (pricing.nonRefundable.available === false && !isReturnTrip)) && (
                                              <div className="flex-fill p-2 d-flex flex-column justify-content-center" style={{ border: '1px solid #ccc', borderRadius: '6px', backgroundColor: '#f8f9fa' }}>
                                                <strong>
                                                  {pricing.currency?.currencyCode || ""}{" "}
                                                  {isReturnTrip
                                                    ? (pricing.returnTotalPrice || 0).toFixed(2)
                                                    : (pricing.totalPrice || 0).toFixed(2)
                                                  }
                                                </strong>
                                              </div>
                                            )}
                                        </div>
                                      ) : (
                                        <span className="text-muted">Price N/A</span>
                                      )}
                                    </td>
                                    <td>
                                      <span className={`status-badge status-${trip.status?.toLowerCase() || "scheduled"}`}>
                                        {trip.status}
                                      </span>
                                      {trip.tripStatusFlags && (
                                        <div className="mt-1" style={{ fontSize: "0.7rem" }}>
                                          {trip.tripStatusFlags.bookingOpen && (
                                            <span className="badge bg-success me-1">Booking Open</span>
                                          )}
                                          {trip.tripStatusFlags.checkInOpen && (
                                            <span className="badge bg-info me-1">Check-in Open</span>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                    <td>
                                      {!selectedTrips.includes(`${trip._id}-${cabin._id}`) && (
                                        <small className="text-muted d-block">
                                          First select any price
                                        </small>
                                      )}
                                      {!bookingAllowed && (
                                        <small className="text-danger d-block" style={{ fontSize: "0.7rem" }}>
                                          Booking Closed
                                        </small>
                                      )}
                                    </td>
                                  </tr>

                                  {/* Expandable details row */}
                                  <tr className="details-row">
                                    <td colSpan={13} className="p-0">
                                      <div
                                        className="trip-details bg-white p-3 border-top"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => toggleExpand(`${trip._id}-${cabin._id}`)}
                                      >
                                        <div className="d-flex justify-content-between align-items-center">
                                          <div>
                                            <p className="mb-1 small">
                                              <strong>Check-in Opens:</strong>{" "}
                                              {formatDateTime(trip.checkInOpeningDate)} |{" "}
                                              <strong>Check-in Closes:</strong>{" "}
                                              {formatDateTime(trip.checkInClosingDate)} |{" "}
                                              <strong>Booking Closes:</strong>{" "}
                                              {formatDateTime(trip.bookingClosingDate)}
                                            </p>

                                          </div>
                                          <button className="btn btn-sm btn-outline-primary">
                                            {expandedTrips[`${trip._id}-${cabin._id}`] ? "Hide Details" : "Show Details"}
                                          </button>
                                        </div>

                                        {expandedTrips[`${trip._id}-${cabin._id}`] && (
                                          <div className="trip-details-expanded mt-3 p-3">
                                            <div className="row">
                                              {/* Outbound Price Breakdown */}
                                              <div className="col-md-6">
                                                <h6>Price Breakdown</h6>
                                                <div className="price-breakdown p-3">
                                                  {(() => {
                                                    let breakdownData = null
                                                    const selectedType = selectedPricingType[`${trip._id}-${cabin._id}`]

                                                    if (selectedType === 'refundable' && pricing?.refundable?.breakdown) {
                                                      breakdownData = { breakdown: pricing.refundable.breakdown, pricing: pricing.refundable }
                                                    } else if (selectedType === 'nonRefundable' && pricing?.nonRefundable?.breakdown) {
                                                      breakdownData = { breakdown: pricing.nonRefundable.breakdown, pricing: pricing.nonRefundable }
                                                    } else if (pricing?.refundable?.breakdown) {
                                                      breakdownData = { breakdown: pricing.refundable.breakdown, pricing: pricing.refundable }
                                                    } else if (pricing?.breakdown) {
                                                      breakdownData = { breakdown: pricing.breakdown, pricing: pricing }
                                                    }

                                                    return breakdownData?.breakdown?.length > 0 ? (
                                                      <>
                                                        {breakdownData.breakdown.map((item, idx) => (
                                                          <div key={idx} className="mb-2 pb-2 border-bottom">
                                                            <div className="d-flex justify-content-between">
                                                              <span>{item.payloadType?.name} x {item.quantity}</span>
                                                              <strong>
                                                                {breakdownData.pricing.currency?.currencyCode}{" "}
                                                                {isReturnTrip
                                                                  ? (item.outboundPrice?.subtotal || 0).toFixed(2)
                                                                  : (item.subtotal || 0).toFixed(2)
                                                                }
                                                              </strong>
                                                            </div>
                                                            <small className="text-muted">
                                                              Unit Price: {(isReturnTrip ? item.outboundPrice?.unitPrice : item.unitPrice || 0).toFixed(2)} | Unit Total:{" "}
                                                              {(isReturnTrip ? item.outboundPrice?.unitTotalPrice : item.unitTotalPrice || 0).toFixed(2)}
                                                            </small>
                                                            <div className="mt-1">
                                                              {(isReturnTrip
                                                                ? (item.outboundPrice?.taxes || item.outboundPrice?.pricingRules?.taxes || [])
                                                                : (item.taxes || [])
                                                              ).map((tax, tIdx) => (
                                                                <small key={tIdx} className="d-block text-muted">
                                                                  {tax.name} ({tax.code}):{" "}
                                                                  {tax.type === "%" ? `${tax.value}%` : tax.value} - {tax.form}
                                                                </small>
                                                              ))}
                                                            </div>
                                                            <div className="mt-2">
                                                              {(isReturnTrip ? item.outboundPrice?.promotion : item.promotion) && (
                                                                <span className="badge bg-success text-white me-2">
                                                                  <i className="bi bi-tag-fill me-1"></i>
                                                                  {(isReturnTrip ? item.outboundPrice?.promotion : item.promotion).promotionName}
                                                                </span>
                                                              )}
                                                            </div>
                                                          </div>
                                                        ))}
                                                        <div className="d-flex justify-content-between fw-bold">
                                                          <span>Total</span>
                                                          <span>
                                                            {breakdownData.pricing.currency?.currencyCode}{" "}
                                                            {isReturnTrip
                                                              ? (breakdownData.pricing.outboundTotal || 0).toFixed(2)
                                                              : (breakdownData.pricing.totalPrice || 0).toFixed(2)
                                                            }
                                                          </span>
                                                        </div>
                                                      </>
                                                    ) : (
                                                      <p className="text-muted mb-0">No pricing breakdown available</p>
                                                    )
                                                  })()}
                                                </div>

                                                {/* Ticketing Rules */}
                                                <div className="mt-3">
                                                  <h6>Ticketing Rules</h6>
                                                  <div className="price-breakdown p-3">
                                                    {ticketingRules && Object.keys(ticketingRules).length > 0 ? (
                                                      <>
                                                        {ticketingRules.REFUND && (
                                                          <div className="mb-2">
                                                            <strong>Refund:</strong> {ticketingRules.REFUND.ruleName}
                                                            <br />
                                                            <small className="text-muted">
                                                              Normal Fee:{" "}
                                                              {ticketingRules.REFUND.normalFee?.type === "FIXED"
                                                                ? `${ticketingRules.REFUND.normalFee.value}`
                                                                : `${ticketingRules.REFUND.normalFee?.value}%`}{" "}
                                                              | No-Show:{" "}
                                                              {ticketingRules.REFUND.noShowPenalty?.type === "PERCENTAGE"
                                                                ? `${ticketingRules.REFUND.noShowPenalty.value}%`
                                                                : ticketingRules.REFUND.noShowPenalty?.value}
                                                            </small>
                                                          </div>
                                                        )}
                                                        {ticketingRules.VOID && (
                                                          <div className="mb-2">
                                                            <strong>Void:</strong> {ticketingRules.VOID.ruleName}
                                                            <br />
                                                            <small className="text-muted">
                                                              Normal Fee:{" "}
                                                              {ticketingRules.VOID.normalFee?.type === "FIXED"
                                                                ? `${ticketingRules.VOID.normalFee.value}`
                                                                : `${ticketingRules.VOID.normalFee?.value}%`}
                                                            </small>
                                                          </div>
                                                        )}
                                                        {ticketingRules.REISSUE && (
                                                          <div className="mb-2">
                                                            <strong>Reissue:</strong> {ticketingRules.REISSUE.ruleName}
                                                            <br />
                                                            <small className="text-muted">
                                                              Normal Fee:{" "}
                                                              {ticketingRules.REISSUE.normalFee?.type === "PERCENTAGE"
                                                                ? `${ticketingRules.REISSUE.normalFee.value}%`
                                                                : ticketingRules.REISSUE.normalFee?.value}
                                                            </small>
                                                          </div>
                                                        )}
                                                      </>
                                                    ) : (
                                                      <p className="text-muted mb-0">No ticketing rules available</p>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>

                                              {/* Right Column: Luggage Allowance & Promotion Info */}
                                              <div className="col-md-6">
                                                <h6>Luggage Allowance</h6>
                                                <div className="price-breakdown p-3">
                                                  {(() => {
                                                    let luggageBreakdown = null
                                                    const selectedType = selectedPricingType[`${trip._id}-${cabin._id}`]

                                                    if (selectedType === 'refundable' && pricing?.refundable?.breakdown) {
                                                      luggageBreakdown = pricing.refundable.breakdown
                                                    } else if (selectedType === 'nonRefundable' && pricing?.nonRefundable?.breakdown) {
                                                      luggageBreakdown = pricing.nonRefundable.breakdown
                                                    } else if (pricing?.refundable?.breakdown) {
                                                      luggageBreakdown = pricing.refundable.breakdown
                                                    } else if (pricing?.breakdown) {
                                                      luggageBreakdown = pricing.breakdown
                                                    }

                                                    return luggageBreakdown && luggageBreakdown.length > 0 ? (
                                                      <>
                                                        {luggageBreakdown.map((item, idx) => (
                                                          <div key={idx} className="mb-3 pb-2 border-bottom">
                                                            <div className="d-flex justify-content-between mb-2">
                                                              <strong>{item.payloadType?.name}</strong>
                                                            </div>
                                                            {isReturnTrip ? (
                                                              <div className="row">
                                                                <div className="col-6">
                                                                  <small className="text-muted">Pieces:</small>
                                                                  <div><strong>{item.outboundPrice?.allowedLuggagePieces || 0}</strong></div>
                                                                </div>
                                                                <div className="col-6">
                                                                  <small className="text-muted">Weight:</small>
                                                                  <div><strong>{item.outboundPrice?.allowedLuggageWeight || 0}kg</strong></div>
                                                                </div>
                                                              </div>
                                                            ) : (
                                                              <div className="row">
                                                                <div className="col-6">
                                                                  <small className="text-muted">Pieces:</small>
                                                                  <div><strong>{item.allowedLuggagePieces || 0}</strong></div>
                                                                </div>
                                                                <div className="col-6">
                                                                  <small className="text-muted">Weight:</small>
                                                                  <div><strong>{item.allowedLuggageWeight || 0}kg</strong></div>
                                                                </div>
                                                              </div>
                                                            )}
                                                            {(isReturnTrip ? item.outboundPrice?.excessLuggagePricePerKg : item.excessLuggagePricePerKg) && (
                                                              <div className="mt-2 pt-2 border-top">
                                                                <small className="text-muted">Excess Luggage:</small>
                                                                <div>
                                                                  <strong>
                                                                    {(() => {
                                                                      const selectedType = selectedPricingType[`${trip._id}-${cabin._id}`]
                                                                      if (selectedType === 'refundable' && pricing?.refundable?.currency) {
                                                                        return pricing.refundable.currency.currencyCode
                                                                      } else if (selectedType === 'nonRefundable' && pricing?.nonRefundable?.currency) {
                                                                        return pricing.nonRefundable.currency.currencyCode
                                                                      }
                                                                      return pricing?.refundable?.currency?.currencyCode || pricing?.currency?.currencyCode || ''
                                                                    })()}{" "}
                                                                    {isReturnTrip ? item.outboundPrice.excessLuggagePricePerKg : item.excessLuggagePricePerKg}/kg
                                                                  </strong>
                                                                </div>
                                                              </div>
                                                            )}
                                                          </div>
                                                        ))}
                                                      </>
                                                    ) : (
                                                      <p className="text-muted mb-0">No luggage information available</p>
                                                    )
                                                  })()}
                                                </div>

                                                {/* Promotion Information */}
                                                {(() => {
                                                  const refundableItem = pricing?.refundable?.breakdown?.[0]
                                                  const nonRefundableItem = pricing?.nonRefundable?.breakdown?.[0]
                                                  const promo = trip.promotion ||
                                                    (isReturnTrip
                                                      ? (refundableItem?.outboundPrice?.promotion || nonRefundableItem?.outboundPrice?.promotion)
                                                      : (refundableItem?.promotion || nonRefundableItem?.promotion || pricing?.breakdown?.[0]?.promotion));
                                                  return promo ? (
                                                    <div className="mt-3">
                                                      <h6>Promotion Information</h6>
                                                      <div className="price-breakdown p-3" style={{ border: '1px dashed #198754', backgroundColor: '#f0f9f7', borderRadius: '8px' }}>
                                                        <div className="mb-2 d-flex justify-content-between align-items-center">
                                                          <span className="badge bg-success text-white">
                                                            <i className="bi bi-tag-fill me-1"></i>
                                                            {promo.promotionName}
                                                          </span>
                                                        </div>
                                                        {promo.calculationType && (
                                                          <div className="mb-2 p-2 rounded" style={{ backgroundColor: '#fff', border: '1px solid #e9ecef' }}>
                                                            {promo.calculationType === 'value' ? (
                                                              <>
                                                                <small className="text-muted d-block" style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Basis: Total Ticket Value</small>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                  <span className="small">Buy Total Value: <strong>{promo.minValue}</strong></span>
                                                                  <span className="small">Gets Discount: <strong>{promo.discountValue}{promo.discountType === 'percentage' ? '%' : ''}</strong></span>
                                                                </div>
                                                              </>
                                                            ) : (
                                                              <>
                                                                <small className="text-muted d-block" style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Basis: Ticket Quantity</small>
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                  <span className="small">Buy X: <strong>{promo.buyX}</strong></span>
                                                                  <span className="small">Get Y: <strong>{promo.getY}</strong></span>
                                                                </div>
                                                              </>
                                                            )}
                                                          </div>
                                                        )}
                                                        {promo.description && (
                                                          <div className="mb-2">
                                                            <small className="text-muted d-block">Description:</small>
                                                            <p className="mb-1 small" style={{ lineHeight: '1.4' }}>{promo.description}</p>
                                                          </div>
                                                        )}
                                                        {promo.promotionConditions && (
                                                          <div>
                                                            <small className="text-muted d-block">Conditions:</small>
                                                            <p className="mb-0 small" style={{ lineHeight: '1.4' }}>{promo.promotionConditions}</p>
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  ) : null;
                                                })()}
                                              </div>
                                            </div>

                                            {/* Trip Status Flags */}
                                            <div className="mt-3">
                                              <h6>Trip Status</h6>
                                              <div className="d-flex gap-2 flex-wrap">
                                                <span className={`badge ${trip.tripStatusFlags?.bookingOpen ? "bg-success" : "bg-secondary"}`}>
                                                  Booking: {trip.tripStatusFlags?.bookingOpen ? "Open" : "Closed"}
                                                </span>
                                                <span className={`badge ${trip.tripStatusFlags?.checkInOpen ? "bg-success" : "bg-secondary"}`}>
                                                  Check-in: {trip.tripStatusFlags?.checkInOpen ? "Open" : "Closed"}
                                                </span>
                                                <span className={`badge ${trip.tripStatusFlags?.boardingClosed ? "bg-danger" : "bg-success"}`}>
                                                  Boarding: {trip.tripStatusFlags?.boardingClosed ? "Closed" : "Open"}
                                                </span>
                                                <span className={`badge ${trip.tripStatusFlags?.departed ? "bg-info" : "bg-secondary"}`}>
                                                  {trip.tripStatusFlags?.departed ? "Departed" : "Not Departed"}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                </React.Fragment>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Inbound Trip Section — only for return trips */}
                  {isReturnTrip && (
                    <div className="card shadow-sm custom-card mb-4">
                      <div className="card-body p-0">
                        <h3 className="card-title p-3 mb-0 border-bottom">
                          Inbound Trip ({inboundDisplayTrips.length} found)
                        </h3>
                        <div className="table-responsive">
                          <table className="table table-borderless align-middle mb-0 custom-results-table">
                            <thead className="text-secondary text-uppercase" style={{ fontSize: "0.8rem" }}>
                              <tr>
                                <th>Ship Name</th>
                                <th>Trip Code</th>
                                <th>Origin</th>
                                <th>Destination</th>
                                <th>ETD</th>
                                <th>ETA</th>
                                <th>Cabin</th>
                                <th>Passenger</th>
                                <th>Visa</th>
                                <th>Available</th>
                                <th>Total Price</th>
                                <th>Status</th>
                                <th>Select</th>
                              </tr>
                            </thead>
                            <tbody>
                              {inboundDisplayTrips.map((tripData, index) => {
                                const trip = tripData.returnTrip?.inbound
                                const cabin = tripData.cabin
const availability = tripData.availability
                                const pricing = tripData.pricing
                                const passengers = tripData.passengers
                                const ticketingRules = tripData.ticketingRules?.inbound || {}
                                const bookingAllowed = tripData.bookingAllowed
                                const pricingType = tripData.pricingType

                                if (!trip) return null

                                const inboundKey = `inbound-${trip._id}-${cabin._id}`

                                return (
                                  <React.Fragment key={`inbound-${trip._id}-${cabin._id}-${pricingType}-${index}`}>
                                    <tr className="trip-row">
                                      <td>
                                        <strong>{trip.ship?.name || "N/A"}</strong>
                                        <br />
                                        <small className="text-muted">{trip.ship?.imoNumber}</small>
                                      </td>
                                      <td>{trip.tripCode}</td>
                                      <td>
                                        {trip.departurePort?.name}
                                        <br />
                                        <small className="text-muted">({trip.departurePort?.code})</small>
                                      </td>
                                      <td>
                                        {trip.arrivalPort?.name}
                                        <br />
                                        <small className="text-muted">({trip.arrivalPort?.code})</small>
                                      </td>
                                      <td>{formatDateTime(trip.departureDateTime)}</td>
                                      <td>{formatDateTime(trip.arrivalDateTime)}</td>
                                      <td>
                                        <strong>{cabin.name}</strong>
                                      </td>
                                      <td>
                                        {passengers?.map((p) => `${p.quantity} ${p.payloadType?.name}`).join(", ") || "N/A"}
                                      </td>
                                      <td>
                                        {searchParams?.visaType || "N/A"}
                                      </td>
                                      <td>
                                        {(() => {
                                          const availSeats = availability?.inboundSeats ?? 0
                                          return (
                                            <span className={`badge ${availSeats > 10 ? "bg-success" : availSeats > 0 ? "bg-warning" : "bg-danger"}`}>
                                              {availSeats}
                                            </span>
                                          )
                                        })()}
                                      </td>
                                      <td>
                                        {pricing ? (
                                          <div className="d-flex gap-2" style={{ minHeight: '70px' }}>
                                            {/* Refundable Price Card — inbound: only show if inboundTotal > 0 */}
                                            {pricing.refundable && pricing.refundable.available &&
                                              (pricing.refundable.inboundTotal ?? 0) > 0 && (
                                                <div
                                                  className="flex-fill p-2 d-flex flex-column justify-content-center"
                                                  style={{
                                                    border: selectedPricingType[inboundKey] === 'refundable' ? '3px solid #28a745' : '1px solid #28a745',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#f0f9f7',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                  }}
                                                  onClick={() => toggleTripSelection(`inbound-${trip._id}`, cabin._id, 'refundable')}
                                                  role="button"
                                                  tabIndex={0}
                                                >
                                                  <span className="badge bg-success text-white mb-1" style={{ width: 'fit-content' }}>Refundable ✓</span>
                                                  <strong style={{ fontSize: '1.1em' }}>
                                                    {pricing.refundable.currency?.currencyCode || ""}{" "}
                                                    {(pricing.refundable.inboundTotal || 0).toFixed(2)}
                                                  </strong>
                                                  {selectedPricingType[inboundKey] === 'refundable' && (
                                                    <span className="badge bg-success mt-2" style={{ width: 'fit-content' }}>Selected</span>
                                                  )}
                                                </div>
                                              )}
                                            {/* Non-Refundable Price Card — inbound: only show if inboundTotal > 0 */}
                                            {pricing.nonRefundable &&
                                              (pricing.nonRefundable.inboundTotal ?? 0) > 0 && (
                                                <div
                                                  className="flex-fill p-2 d-flex flex-column justify-content-center"
                                                  style={{
                                                    border: selectedPricingType[inboundKey] === 'nonRefundable' ? '3px solid #ffc107' : '1px solid #ffc107',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#fff8f0',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s'
                                                  }}
                                                  onClick={() => toggleTripSelection(`inbound-${trip._id}`, cabin._id, 'nonRefundable')}
                                                  role="button"
                                                  tabIndex={0}
                                                >
                                                  <div className="mb-1">
                                                    <span className="badge bg-warning text-dark" style={{ width: 'fit-content' }}>Non-Refundable</span>
                                                    {pricing.nonRefundable.hasMissingPrice && (
                                                      <span className="badge bg-danger text-white ms-1">Partial</span>
                                                    )}
                                                  </div>
                                                  <strong style={{ fontSize: '1.1em' }}>
                                                    {pricing.nonRefundable.currency?.currencyCode || ""}{" "}
                                                    {(pricing.nonRefundable.inboundTotal || 0).toFixed(2)}
                                                  </strong>
                                                  {selectedPricingType[inboundKey] === 'nonRefundable' && (
                                                    <span className="badge bg-warning text-dark mt-2" style={{ width: 'fit-content' }}>Selected</span>
                                                  )}
                                                </div>
                                              )}
                                          </div>
                                        ) : (
                                          <span className="text-muted">Price N/A</span>
                                        )}
                                      </td>
                                      <td>
                                        <span className={`status-badge status-${trip.status?.toLowerCase() || "scheduled"}`}>
                                          {trip.status}
                                        </span>
                                        {trip.tripStatusFlags && (
                                          <div className="mt-1" style={{ fontSize: "0.7rem" }}>
                                            {trip.tripStatusFlags.bookingOpen && (
                                              <span className="badge bg-success me-1">Booking Open</span>
                                            )}
                                            {trip.tripStatusFlags.checkInOpen && (
                                              <span className="badge bg-info me-1">Check-in Open</span>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                      <td>
                                        {!selectedTrips.includes(inboundKey) && (
                                          <small className="text-muted d-block">First select any price</small>
                                        )}
                                        {!bookingAllowed && (
                                          <small className="text-danger d-block" style={{ fontSize: "0.7rem" }}>
                                            Booking Closed
                                          </small>
                                        )}
                                      </td>
                                    </tr>

                                    {/* Inbound expandable details row */}
                                    <tr className="details-row">
                                      <td colSpan={13} className="p-0">
                                        <div
                                          className="trip-details bg-white p-3 border-top"
                                          style={{ cursor: "pointer" }}
                                          onClick={() => toggleExpand(inboundKey)}
                                        >
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                              <p className="mb-1 small">
                                                <strong>Check-in Opens:</strong>{" "}
                                                {formatDateTime(trip.checkInOpeningDate)} |{" "}
                                                <strong>Check-in Closes:</strong>{" "}
                                                {formatDateTime(trip.checkInClosingDate)} |{" "}
                                                <strong>Booking Closes:</strong>{" "}
                                                {formatDateTime(trip.bookingClosingDate)}
                                              </p>

                                            </div>
                                            <button className="btn btn-sm btn-outline-primary">
                                              {expandedTrips[inboundKey] ? "Hide Details" : "Show Details"}
                                            </button>
                                          </div>

                                          {expandedTrips[inboundKey] && (
                                            <div className="trip-details-expanded mt-3 p-3">
                                              <div className="row">
                                                {/* Inbound Price Breakdown */}
                                                <div className="col-md-6">
                                                  <h6>Price Breakdown</h6>
                                                  <div className="price-breakdown p-3">
                                                    {(() => {
                                                      let breakdownData = null
                                                      const selectedType = selectedPricingType[inboundKey]
                                                      if (selectedType === 'refundable' && pricing?.refundable?.breakdown) {
                                                        breakdownData = { breakdown: pricing.refundable.breakdown, pricing: pricing.refundable, totalKey: 'inboundTotal' }
                                                      } else if (selectedType === 'nonRefundable' && pricing?.nonRefundable?.breakdown) {
                                                        breakdownData = { breakdown: pricing.nonRefundable.breakdown, pricing: pricing.nonRefundable, totalKey: 'inboundTotal' }
                                                      } else if (pricing?.refundable?.breakdown) {
                                                        breakdownData = { breakdown: pricing.refundable.breakdown, pricing: pricing.refundable, totalKey: 'inboundTotal' }
                                                      } else if (pricing?.breakdown) {
                                                        breakdownData = { breakdown: pricing.breakdown, pricing: pricing, totalKey: 'inboundTotal' }
                                                      }
                                                      return breakdownData?.breakdown?.length > 0 ? (
                                                        <>
                                                          {breakdownData.breakdown.map((item, idx) => (
                                                            <div key={idx} className="mb-2 pb-2 border-bottom">
                                                              <div className="d-flex justify-content-between">
                                                                <span>{item.payloadType?.name} x {item.quantity}</span>
                                                                <strong>
                                                                  {breakdownData.pricing.currency?.currencyCode}{" "}
                                                                  {(item.inboundPrice?.subtotal || item.subtotal || 0).toFixed(2)}
                                                                </strong>
                                                              </div>
                                                              <small className="text-muted">
                                                                Unit Price: {(item.inboundPrice?.unitPrice || 0).toFixed(2)} | Unit Total:{" "}
                                                                {(item.inboundPrice?.unitTotalPrice || 0).toFixed(2)}
                                                              </small>
                                                              <div className="mt-1">
                                                                {(item.inboundPrice?.taxes || item.inboundPrice?.pricingRules?.taxes || []).map((tax, tIdx) => (
                                                                  <small key={tIdx} className="d-block text-muted">
                                                                    {tax.name} ({tax.code}):{" "}
                                                                    {tax.type === "%" ? `${tax.value}%` : tax.value} - {tax.form}
                                                                  </small>
                                                                ))}
                                                              </div>
                                                              <div className="mt-2">
                                                                {item.inboundPrice?.promotion && (
                                                                  <span className="badge bg-success text-white me-2">
                                                                    <i className="bi bi-tag-fill me-1"></i>
                                                                    {item.inboundPrice.promotion.promotionName}
                                                                  </span>
                                                                )}

                                                              </div>
                                                            </div>
                                                          ))}
                                                          <div className="d-flex justify-content-between fw-bold">
                                                            <span>Total</span>
                                                            <span>
                                                              {breakdownData.pricing.currency?.currencyCode}{" "}
                                                              {(breakdownData.pricing.inboundTotal || 0).toFixed(2)}
                                                            </span>
                                                          </div>
                                                        </>
                                                      ) : (
                                                        <p className="text-muted mb-0">No pricing breakdown available</p>
                                                      )
                                                    })()}
                                                  </div>

                                                  {/* Inbound Ticketing Rules */}
                                                  <div className="mt-3">
                                                    <h6>Ticketing Rules</h6>
                                                    <div className="price-breakdown p-3">
                                                      {ticketingRules && Object.keys(ticketingRules).length > 0 ? (
                                                        <>
                                                          {ticketingRules.REFUND && (
                                                            <div className="mb-2">
                                                              <strong>Refund:</strong> {ticketingRules.REFUND.ruleName}
                                                              <br />
                                                              <small className="text-muted">
                                                                Normal Fee:{" "}
                                                                {ticketingRules.REFUND.normalFee?.type === "FIXED"
                                                                  ? `${ticketingRules.REFUND.normalFee.value}`
                                                                  : `${ticketingRules.REFUND.normalFee?.value}%`}{" "}
                                                                | No-Show:{" "}
                                                                {ticketingRules.REFUND.noShowPenalty?.type === "PERCENTAGE"
                                                                  ? `${ticketingRules.REFUND.noShowPenalty.value}%`
                                                                  : ticketingRules.REFUND.noShowPenalty?.value}
                                                              </small>
                                                            </div>
                                                          )}
                                                          {ticketingRules.VOID && (
                                                            <div className="mb-2">
                                                              <strong>Void:</strong> {ticketingRules.VOID.ruleName}
                                                              <br />
                                                              <small className="text-muted">
                                                                Normal Fee:{" "}
                                                                {ticketingRules.VOID.normalFee?.type === "FIXED"
                                                                  ? `${ticketingRules.VOID.normalFee.value}`
                                                                  : `${ticketingRules.VOID.normalFee?.value}%`}
                                                              </small>
                                                            </div>
                                                          )}
                                                          {ticketingRules.REISSUE && (
                                                            <div className="mb-2">
                                                              <strong>Reissue:</strong> {ticketingRules.REISSUE.ruleName}
                                                              <br />
                                                              <small className="text-muted">
                                                                Normal Fee:{" "}
                                                                {ticketingRules.REISSUE.normalFee?.type === "PERCENTAGE"
                                                                  ? `${ticketingRules.REISSUE.normalFee.value}%`
                                                                  : ticketingRules.REISSUE.normalFee?.value}
                                                              </small>
                                                            </div>
                                                          )}
                                                        </>
                                                      ) : (
                                                        <p className="text-muted mb-0">No ticketing rules available</p>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>

                                                {/* Inbound Right Column: Luggage & Promotion */}
                                                <div className="col-md-6">
                                                  <h6>Luggage Allowance</h6>
                                                  <div className="price-breakdown p-3">
                                                    {(() => {
                                                      let luggageBreakdown = null
                                                      const selectedType = selectedPricingType[inboundKey]
                                                      if (selectedType === 'refundable' && pricing?.refundable?.breakdown) {
                                                        luggageBreakdown = pricing.refundable.breakdown
                                                      } else if (selectedType === 'nonRefundable' && pricing?.nonRefundable?.breakdown) {
                                                        luggageBreakdown = pricing.nonRefundable.breakdown
                                                      } else if (pricing?.refundable?.breakdown) {
                                                        luggageBreakdown = pricing.refundable.breakdown
                                                      } else if (pricing?.breakdown) {
                                                        luggageBreakdown = pricing.breakdown
                                                      }
                                                      return luggageBreakdown && luggageBreakdown.length > 0 ? (
                                                        <>
                                                          {luggageBreakdown.map((item, idx) => (
                                                            <div key={idx} className="mb-3 pb-2 border-bottom">
                                                              <div className="d-flex justify-content-between mb-2">
                                                                <strong>{item.payloadType?.name}</strong>
                                                              </div>
                                                              <div className="row">
                                                                <div className="col-6">
                                                                  <small className="text-muted">Pieces:</small>
                                                                  <div><strong>{item.inboundPrice?.allowedLuggagePieces || item.allowedLuggagePieces || 0}</strong></div>
                                                                </div>
                                                                <div className="col-6">
                                                                  <small className="text-muted">Weight:</small>
                                                                  <div><strong>{item.inboundPrice?.allowedLuggageWeight || item.allowedLuggageWeight || 0}kg</strong></div>
                                                                </div>
                                                              </div>
                                                              {item.inboundPrice?.excessLuggagePricePerKg && (
                                                                <div className="mt-2 pt-2 border-top">
                                                                  <small className="text-muted">Excess Luggage:</small>
                                                                  <div>
                                                                    <strong>
                                                                      {(() => {
                                                                        const selectedType = selectedPricingType[inboundKey]
                                                                        if (selectedType === 'refundable' && pricing?.refundable?.currency) return pricing.refundable.currency.currencyCode
                                                                        if (selectedType === 'nonRefundable' && pricing?.nonRefundable?.currency) return pricing.nonRefundable.currency.currencyCode
                                                                        return pricing?.refundable?.currency?.currencyCode || pricing?.currency?.currencyCode || ''
                                                                      })()}{" "}
                                                                      {item.inboundPrice.excessLuggagePricePerKg}/kg
                                                                    </strong>
                                                                  </div>
                                                                </div>
                                                              )}
                                                            </div>
                                                          ))}
                                                        </>
                                                      ) : (
                                                        <p className="text-muted mb-0">No luggage information available</p>
                                                      )
                                                    })()}
                                                  </div>

                                                  {/* Promotion Information */}
                                                  {(() => {
                                                    const promo = trip.promotion || (pricing?.refundable?.breakdown?.[0]?.promotion) || (pricing?.nonRefundable?.breakdown?.[0]?.promotion) || (pricing?.breakdown?.[0]?.promotion);
                                                    return promo ? (
                                                      <div className="mt-3">
                                                        <h6>Promotion Information</h6>
                                                        <div className="price-breakdown p-3" style={{ border: '1px dashed #198754', backgroundColor: '#f0f9f7', borderRadius: '8px' }}>
                                                          <div className="mb-2 d-flex justify-content-between align-items-center">
                                                            <span className="badge bg-success text-white">
                                                              <i className="bi bi-tag-fill me-1"></i>
                                                              {promo.promotionName}
                                                            </span>
                                                          </div>
                                                          {promo.calculationType && (
                                                            <div className="mb-2 p-2 rounded" style={{ backgroundColor: '#fff', border: '1px solid #e9ecef' }}>
                                                              {promo.calculationType === 'value' ? (
                                                                <>
                                                                  <small className="text-muted d-block" style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Basis: Total Ticket Value</small>
                                                                  <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="small">Buy Total Value: <strong>{promo.minValue}</strong></span>
                                                                    <span className="small">Gets Discount: <strong>{promo.discountValue}{promo.discountType === 'percentage' ? '%' : ''}</strong></span>
                                                                  </div>
                                                                </>
                                                              ) : (
                                                                <>
                                                                  <small className="text-muted d-block" style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}>Basis: Ticket Quantity</small>
                                                                  <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="small">Buy X: <strong>{promo.buyX}</strong></span>
                                                                    <span className="small">Get Y: <strong>{promo.getY}</strong></span>
                                                                  </div>
                                                                </>
                                                              )}
                                                            </div>
                                                          )}
                                                          {promo.description && (
                                                            <div className="mb-2">
                                                              <small className="text-muted d-block">Description:</small>
                                                              <p className="mb-1 small" style={{ lineHeight: '1.4' }}>{promo.description}</p>
                                                            </div>
                                                          )}
                                                          {promo.promotionConditions && (
                                                            <div>
                                                              <small className="text-muted d-block">Conditions:</small>
                                                              <p className="mb-0 small" style={{ lineHeight: '1.4' }}>{promo.promotionConditions}</p>
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    ) : null;
                                                  })()}
                                                </div>
                                              </div>

                                              {/* Inbound Trip Status */}
                                              <div className="mt-3">
                                                <h6>Trip Status</h6>
                                                <div className="d-flex gap-2 flex-wrap">
                                                  <span className={`badge ${trip.tripStatusFlags?.bookingOpen ? "bg-success" : "bg-secondary"}`}>
                                                    Booking: {trip.tripStatusFlags?.bookingOpen ? "Open" : "Closed"}
                                                  </span>
                                                  <span className={`badge ${trip.tripStatusFlags?.checkInOpen ? "bg-success" : "bg-secondary"}`}>
                                                    Check-in: {trip.tripStatusFlags?.checkInOpen ? "Open" : "Closed"}
                                                  </span>
                                                  <span className={`badge ${trip.tripStatusFlags?.boardingClosed ? "bg-danger" : "bg-success"}`}>
                                                    Boarding: {trip.tripStatusFlags?.boardingClosed ? "Closed" : "Open"}
                                                  </span>
                                                  <span className={`badge ${trip.tripStatusFlags?.departed ? "bg-info" : "bg-secondary"}`}>
                                                    {trip.tripStatusFlags?.departed ? "Departed" : "Not Departed"}
                                                  </span>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  </React.Fragment>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <div className="modal modal-lg" id="myModal1">
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Travel Document Details</h4>
              <button type="button" className="btn-close" data-bs-dismiss="modal" style={{ background: "#fff" }}>
                X
              </button>
            </div>
            <div className="modal-body">
              <div className="form-section" id="passenger-details-form" style={{ display: "block" }}>
                <h5>Passenger Details</h5>
                <p className="text-muted">
                  Please fill in the passenger details for {selectedTrips.length} selected trip(s).
                </p>
                <div id="passenger-forms-container">
                  {searchParams?.passengers?.map((passenger, pIdx) => {
                    const payloadType = payloadTypes?.find((p) => p._id === passenger.payloadTypeId)
                    return Array.from({ length: passenger.quantity }).map((_, idx) => (
                      <div key={`${pIdx}-${idx}`} className="passenger-form border-bottom pb-3 mb-3">
                        <h6>
                          {payloadType?.name || "Passenger"} {idx + 1}
                        </h6>
                        <div className="row">
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Name</label>
                            <input className="form-control" type="text" required onChange={(e) => handlePassengerChange(pIdx, idx, "name", e.target.value, payloadType?.name || "Passenger")} />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Gender</label>
                            <select className="form-control" required onChange={(e) => handlePassengerChange(pIdx, idx, "gender", e.target.value, payloadType?.name || "Passenger")}>
                              <option value="">Select Gender</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                            </select>
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Age</label>
                            <input
                              className="form-control"
                              type="number"
                              min={0}
                              max={120}
                              required
                              value={passengerFormData[`${pIdx}-${idx}`]?.age ?? ""}
                              onChange={(e) => handlePassengerChange(pIdx, idx, "age", e.target.value, payloadType?.name || "Passenger")}
                              placeholder="Auto-filled from DOB"
                              style={{ backgroundColor: passengerFormData[`${pIdx}-${idx}`]?.age !== undefined ? "#e8f5e9" : "" }}
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Nationality</label>
                            <select className="form-control" required onChange={(e) => handlePassengerChange(pIdx, idx, "nationality", e.target.value, payloadType?.name || "Passenger")}>
                              <option value="">Select Nationality</option>
                              {countries.map(c => (
                                <option key={c._id} value={c.name}>{c.name}</option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Passport No</label>
                            <input className="form-control" type="text" required onChange={(e) => handlePassengerChange(pIdx, idx, "passportNo", e.target.value, payloadType?.name || "Passenger")} />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Date of Birth</label>
                            <input
                              className="form-control"
                              type="date"
                              required
                              max={new Date().toISOString().split("T")[0]}
                              onChange={(e) => handlePassengerChange(pIdx, idx, "birthDate", e.target.value, payloadType?.name || "Passenger")}
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Place of Birth</label>
                            <input className="form-control" type="text" required onChange={(e) => handlePassengerChange(pIdx, idx, "placeOfBirth", e.target.value, payloadType?.name || "Passenger")} />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Passport Expiry Date</label>
                            <input className="form-control" type="date" required onChange={(e) => handlePassengerChange(pIdx, idx, "passportExpiryDate", e.target.value, payloadType?.name || "Passenger")} />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Email Address</label>
                            <input className="form-control" type="email" required onChange={(e) => handlePassengerChange(pIdx, idx, "email", e.target.value, payloadType?.name || "Passenger")} />
                          </div>
                        </div>
                      </div>
                    ))
                  })}
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">
                    Cancel
                  </button>
                  {!isB2C && (
                    <button type="button" className="btn btn-warning text-dark" style={{ border: "none" }} onClick={() => onSubmitBooking(true)} disabled={isBooking}>
                      {isBooking ? "Processing..." : "Create Temporary Booking (Hold)"}
                    </button>
                  )}
                  <button type="button" className="btn text-white" style={{ backgroundColor: "#0b2046", border: "none" }} onClick={() => onSubmitBooking(isB2C ? true : false)} disabled={isBooking}>
                    {isBooking ? "Processing..." : isB2C ? "Confirm Booking Request" : "Proceed to Payment"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer socialMedia={companyInfo?.socialMedia} />
    </>
  )
}

export default SearchResultPaasenger
